import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FaUtensils, FaPlus, FaMinus } from 'react-icons/fa';
import { svg } from '../assets/svg';
import { components } from '../components';
import { hooks } from '../hooks';
import { RootState } from '../store';
import { actions } from '../store/actions';
import { DishType } from '../types';
import { getItemFromLocalStorage } from '../utils/getItemFromLocalStorage';

type Props = {
  index: number;
  dish: DishType;
  isLast: boolean;
};

export const RecomendedItem: React.FC<Props> = ({ dish }) => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();
  const wishlist = useSelector((state: RootState) => state.wishlistSlice);
  
  // State for toggling between Full and Half price
  const [isFullPrice, setIsFullPrice] = useState(true);
  
  // Memoized config to avoid redundant local storage reads
  const MENU_CONFIG = useMemo(() => getItemFromLocalStorage("menuConfiguration"), []);

  const { addToCart, getDishQty, removeFromCart } = hooks.useCartHandler();
  const isInWishlist = wishlist.list.some((item) => item.id === dish.id);
  const quantity = getDishQty(dish.id ?? 0, isFullPrice);

  const toggleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInWishlist) {
      dispatch(actions.removeFromWishlist(dish));
    } else {
      dispatch(actions.addToWishlist(dish));
    }
  };

  const handleCardClick = () => {
    if (dish.availability) {
      navigate(`/dish/${dish.id}`, { state: { dish } });
    }
  };

  return (
    <div
      style={styles.card(dish.availability)}
      onClick={handleCardClick}
      className="menu-item-card"
    >
      {/* Top Section: Media & Badges */}
      <div style={styles.imageWrapper}>
        {dish.image ? (
          <img src={dish.image} alt={dish.name} style={styles.image} />
        ) : (
          <div style={styles.placeholder}>
            <FaUtensils style={{ color: '#ced4da', fontSize: 32 }} />
          </div>
        )}
        
        {/* Floating Overlay Icons */}
        <div style={styles.imageOverlayTop}>
          {/* Veg/Non-Veg Badge - Professional Food Labeling */}
          {dish.availability && (
            <div style={styles.dietaryTag}>
              {dish.category === "Veg" ? (
                <div style={styles.vegBadge}>
                  <div style={styles.vegCircle} />
                </div>
              ) : (
                <div style={styles.nonVegBadge}>
                  <div style={styles.nonVegTriangle} />
                </div>
              )}
            </div>
          )}
          
          {MENU_CONFIG?.isOrderFromAppSupported && dish.availability && (
            <button style={styles.wishlistCircle} onClick={toggleWishlist}>
              <svg.HeartSvg dish={dish} />
            </button>
          )}
        </div>

        {!dish.availability && (
          <div style={styles.soldOutOverlay}>
            <span style={styles.soldOutText}>Sold Out</span>
          </div>
        )}
      </div>

      {/* Bottom Section: Content */}
      <div style={styles.details}>
        <div style={{ flex: 1 }}>
          <h4 style={styles.dishName}>{dish.name}</h4>
          <p style={styles.description}>
            {dish.description || 'Chef’s special selection'}
          </p>
        </div>

        {/* Pricing & Portion Row */}
        <div style={styles.priceRow}>
          <components.Price 
            dish={dish} 
            isFullPrice={isFullPrice}
            containerStyle={{ margin: 0 }}
          />
          
          {dish.halfPrice !== 0 && dish.availability && (
            <div 
              style={styles.portionToggle} 
              onClick={(e) => { e.stopPropagation(); setIsFullPrice(!isFullPrice); }}
            >
              {isFullPrice ? 'Full' : 'Half'}
            </div>
          )}
        </div>

        {/* Professional Action Area */}
        {MENU_CONFIG?.isAddtoCartEnabled && dish.availability && (
          <div style={styles.actionArea}>
            {quantity === 0 ? (
              <button 
                style={styles.addButton}
                onClick={(e) => {
                  e.stopPropagation();
                  addToCart({ ...dish, isFullPrice }, e);
                }}
              >
                ADD <FaPlus size={10} style={{ marginLeft: 8 }} />
              </button>
            ) : (
              <div style={styles.stepperContainer} onClick={(e) => e.stopPropagation()}>
                <button 
                  style={styles.stepBtn} 
                  onClick={(e) => removeFromCart(dish, e)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <FaMinus size={14} style={{ color: '#ffffff', strokeWidth: 3 }} />
                </button>
                <span style={styles.qtyText}>{quantity}</span>
                <button 
                  style={styles.stepBtn} 
                  onClick={(e) => addToCart({ ...dish, isFullPrice }, e)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <FaPlus size={14} style={{ color: '#ffffff', strokeWidth: 3 }} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Modern Delivery App Styles ---
const styles = {
  card: (available: boolean): React.CSSProperties => ({
    width: 175,
    borderRadius: 18,
    backgroundColor: '#ffffff',
    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    cursor: available ? 'pointer' : 'default',
    border: '1px solid #f2f2f2',
    transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  }),
  imageWrapper: {
    height: 125,
    width: '100%',
    position: 'relative' as const,
    backgroundColor: '#f8f9fa',
  },
  image: { width: '100%', height: '100%', objectFit: 'cover' as const },
  placeholder: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)'
  },
  imageOverlayTop: {
    position: 'absolute' as const,
    top: 10,
    left: 10,
    right: 10,
    display: 'flex',
    justifyContent: 'space-between',
    zIndex: 2
  },
  dietaryTag: {
    padding: '3px',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderRadius: '3px',
    border: '1.5px solid rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    width: 28,
    height: 28,
  },
  vegBadge: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '2px',
    border: '2px solid #2ecc71',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
  },
  vegCircle: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#2ecc71',
    border: '1.5px solid #2ecc71',
  },
  nonVegBadge: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '2px',
    border: '2px solid #e74c3c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
  },
  nonVegTriangle: {
    width: 0,
    height: 0,
    borderLeft: '4px solid transparent',
    borderRight: '4px solid transparent',
    borderTop: '7px solid #e74c3c',
    borderBottom: 'none',
  },
  wishlistCircle: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#fff',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
    cursor: 'pointer'
  },
  details: { padding: '12px', flex: 1, display: 'flex', flexDirection: 'column' as const },
  dishName: { 
    fontSize: 14, 
    fontWeight: 700, 
    margin: '0 0 2px 0', 
    color: '#1e272e',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden'
  },
  description: { 
    fontSize: 10, 
    color: '#7f8c8d', 
    margin: '0 0 10px 0',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden'
  },
  priceRow: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    marginBottom: 12 
  },
  portionToggle: {
    fontSize: 9,
    fontWeight: 700,
    color: 'var(--main-turquoise)',
    backgroundColor: '#f0fbfc',
    padding: '2px 6px',
    borderRadius: 4,
    border: '1px solid rgba(0,176,185,0.1)',
    textTransform: 'uppercase' as const
  },
  actionArea: { marginTop: 'auto' },
  addButton: {
    width: '100%',
    padding: '8px 0',
    borderRadius: 8,
    backgroundColor: '#fff',
    border: '1.5px solid var(--main-turquoise)',
    color: 'var(--main-turquoise)',
    fontWeight: 800,
    fontSize: 12,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    letterSpacing: '0.5px'
  },
  stepperContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'var(--main-turquoise)',
    borderRadius: 10,
    padding: '4px 6px',
    boxShadow: '0 4px 12px rgba(0, 176, 185, 0.3)',
    gap: 8,
    minWidth: 100,
  },
  stepBtn: {
    background: 'transparent',
    border: 'none',
    color: '#fff',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    transition: 'all 0.2s ease',
    flexShrink: 0,
  },
  qtyText: { 
    fontWeight: 700, 
    fontSize: 15, 
    color: '#fff',
    minWidth: 20,
    textAlign: 'center' as const,
    fontFamily: 'DM Sans, sans-serif',
  },
  soldOutOverlay: {
    position: 'absolute' as const,
    inset: 0,
    backgroundColor: 'rgba(255,255,255,0.7)',
    backdropFilter: 'grayscale(1) blur(1px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5
  },
  soldOutText: {
    backgroundColor: '#2d3436',
    color: '#fff',
    fontSize: 9,
    fontWeight: 800,
    padding: '4px 10px',
    borderRadius: 4,
    textTransform: 'uppercase' as const
  }
};